import { IStep, INamedEntity } from './general';
import { IUser, UserFields } from './user';
import { IAddress, getFallbackAddress, addressFieldRules } from './address';
import { CompanyStatus } from './resource';
import { CompanyRole, CompanyProjectStatus } from './project';
import { UserModel, WorkerProfileModel } from '.';

export enum ClientFields {
  NAME = 'name',
  DEVELOPER = 'isDeveloper',
  TAXPAYER_IDENTIFICATION_NUMBER = 'taxpayerIdentificationNumber',
  MWBE_TYPE = 'mwbeTypeId',
  TRADES = 'trades',
  OTHER_TRADE = 'otherTrade',
  USERS = 'users',
  ADMIN_USER = 'user',
  BILLING_ADDRESS = 'billingAddress',
  MAILING_ADDRESS = 'mailingAddress',
  SAME_ADDRESS = 'mailingAddressMatchesBillingAddress',
  UNIVERSAL_BADGE_PRICE = 'universalBadgePrice',
}

export interface IClient {
  id: string;
  [ClientFields.NAME]: string;
  [ClientFields.DEVELOPER]: boolean;
  [ClientFields.TAXPAYER_IDENTIFICATION_NUMBER]?: string;
  [ClientFields.MWBE_TYPE]?: string;
  [ClientFields.TRADES]: INamedEntity[];
  [ClientFields.OTHER_TRADE]: string;
  [ClientFields.USERS]?: IUser[];
  [ClientFields.ADMIN_USER]?: IUser;
  [ClientFields.BILLING_ADDRESS]: IAddress;
  [ClientFields.MAILING_ADDRESS]: IAddress;
  [ClientFields.SAME_ADDRESS]: boolean;
  hasUniversalBadge: boolean;
  [ClientFields.UNIVERSAL_BADGE_PRICE]: number;
  status: CompanyStatus;
  hasPaymentMethod?: string;
  taxCondition?: any;
  isTaxExempt?: boolean;
  isPatchRequest?: boolean;
  resendInvitation?: boolean;
  project?: { id: string; name: string };
  sponsor?: { id: string; name: string };
  companyProject?: { id: string; name: string };
  sponsorCompany?: { id: string; name: string };
  projectId?: string;
  sponsorId?: string;
  invitationSentDate?: string;
  projectAcceptedDate?: string;
  projectName?: string;
  relationshipName: string;
  reportsTo: string;
  reportsToName: string;
  projectsCount: number;
  companyLabel: number;
  companyType: CompanyType;
  invitedToFreedomByCompany?: string;
  setupNotes?: string;
  workerCustomProfileFields?: WorkerProfileModel.IWorkerProfileField[];
  sponsoredCompaniesIds?: string[];
  jointVentureCompanies?: { label: string; value: string }[];
  jointVentureCompanyIds?: string[];
  jointVentures?: { name: string; id: string }[];
  impersonatedAdminUsers?: IUser[];
  excludeShellCompanies?: boolean;
  isAlreadyOnProject?: boolean;
  projects?: Array<IProjectToInvite>;
  oldProjects?: Array<IProjectToInvite>;
  phoneNumber?: string;
  isVerified?: boolean;
}

export interface IProjectToInvite {
  projectId?: string;
  project?: { id: string; name: string };
  sponsorId?: string;
  sponsor?: { id: string; name: string };
  taxCondition?: any;
  phoneNumber?: string;
  isVerified?: boolean;
}

export interface IBranch {
  id: string;
  name: string;
  clientId?: string;
  branches?: IBranch[];
  isCurrent?: boolean;
  isParent?: boolean;
  status?: number;
}

export interface IClientProject extends IClient {
  companyProjectStatus: CompanyProjectStatus;
  jobSiteAddress: IAddress;
  role: CompanyRole;
  badgeFields?: any;
  projectBadgeTemplates?: any;
  invitedToProjectByCompany?: string;
}

export interface IHirearchyClientProject extends Partial<IClientProject> {
  level: number;
  sponsoredCompaniesIds: string[];
  trades: INamedEntity[];
  createdAt?: string;
}

export enum ClientStep {
  GENERAL_INFORMATION = 'general-information',
  ADDRESSES = 'addresses',
  USERS = 'users',
  REVIEW = 'review',
  CONSENT_FORM_FIELDS = 'consent-form-fields',
  WORKER_PROFILE_FIELDS = 'worker-profile-fields',
}

export enum ConsentFormFieldType {
  All = 0,
  Standard = 1,
  Custom = 2,
}

export enum CompanyLabel {
  HO = 1,
  B = 2,
  SB = 3,
  JV = 4,
}

export enum CompanyType {
  Standalone = 0,
  HeadOffice = 1,
  Branch = 2,
  JointVenture = 3,
}

export interface IStepMap {
  [key: string]: IStep;
}

export const clientStepMap: IStepMap = {
  [ClientStep.GENERAL_INFORMATION]: {
    key: ClientStep.GENERAL_INFORMATION,
    title: 'General Information',
    subtitle: 'Create New Company',
    hideControls: false,
    order: 0,
    fields: [
      { name: ClientFields.NAME, required: true },
      { name: ClientFields.DEVELOPER, required: false },
      { name: ClientFields.TAXPAYER_IDENTIFICATION_NUMBER, required: true },
      { name: ClientFields.MWBE_TYPE, required: true, computePositive: true },
      { name: ClientFields.TRADES, required: true },
      { name: ClientFields.OTHER_TRADE, required: false },
      { name: ClientFields.UNIVERSAL_BADGE_PRICE, required: false },
    ],
  },
  [ClientStep.CONSENT_FORM_FIELDS]: {
    key: ClientStep.CONSENT_FORM_FIELDS,
    title: 'Consent Form Fields',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 1,
    fields: [],
  },
  [ClientStep.WORKER_PROFILE_FIELDS]: {
    key: ClientStep.WORKER_PROFILE_FIELDS,
    title: 'Worker Profile Fields',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 2,
    fields: [],
  },
  [ClientStep.ADDRESSES]: {
    key: ClientStep.ADDRESSES,
    title: 'Addresses',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 3,
    fields: [
      {
        name: ClientFields.BILLING_ADDRESS,
        required: true,
        fields: addressFieldRules.required,
      },
      {
        name: ClientFields.MAILING_ADDRESS,
        required: false,
        fields: addressFieldRules.notRequired,
      },
    ],
  },
  [ClientFields.USERS]: {
    key: ClientStep.USERS,
    title: 'Users',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 4,
    fields: [
      {
        name: ClientFields.USERS,
        required: true,
        fields: [
          { name: UserFields.FIRST_NAME, required: true },
          { name: UserFields.LAST_NAME, required: true },
          { name: UserFields.EMAIL, required: false },
          { name: UserFields.TITLE, required: false },
          { name: UserFields.MOBILE_PHONE_NUMBER, required: false },
          { name: UserFields.OFFICE_PHONE_NUMBER, required: false },
          { name: UserFields.PREFERRED_CONTACT_METHOD, required: true },
          { name: UserFields.GROUP_IDS, required: false },
        ],
      },
    ],
  },
  [ClientStep.REVIEW]: {
    key: ClientStep.REVIEW,
    title: 'Review',
    subtitle: 'My Company LCC',
    hideControls: true,
    order: 5,
    fields: [],
  },
};

export const onboardingStepMap: IStepMap = {
  [ClientStep.GENERAL_INFORMATION]: {
    key: ClientStep.GENERAL_INFORMATION,
    title: 'General Information',
    subtitle: 'Create New Company',
    hideControls: false,
    order: 0,
    fields: [
      { name: ClientFields.NAME, required: true },
      { name: ClientFields.DEVELOPER, required: false },
      { name: ClientFields.TAXPAYER_IDENTIFICATION_NUMBER, required: true },
      { name: ClientFields.MWBE_TYPE, required: true, computePositive: true },
      { name: ClientFields.TRADES, required: true },
      { name: ClientFields.OTHER_TRADE, required: false },
      { name: ClientFields.UNIVERSAL_BADGE_PRICE, required: false },
    ],
  },
  [ClientStep.ADDRESSES]: {
    key: ClientStep.ADDRESSES,
    title: 'Addresses',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 1,
    fields: [
      {
        name: ClientFields.BILLING_ADDRESS,
        required: true,
        fields: addressFieldRules.required,
      },
      {
        name: ClientFields.MAILING_ADDRESS,
        required: false,
        fields: addressFieldRules.notRequired,
      },
    ],
  },
  [ClientStep.REVIEW]: {
    key: ClientStep.REVIEW,
    title: 'Review',
    subtitle: 'My Company LCC',
    hideControls: true,
    order: 3,
    fields: [],
  },
};

export const pendingApprovalStepMap: IStepMap = {
  [ClientStep.GENERAL_INFORMATION]: {
    key: ClientStep.GENERAL_INFORMATION,
    title: 'General Information',
    subtitle: 'Create New Company',
    hideControls: false,
    order: 0,
    fields: [
      { name: ClientFields.NAME, required: true },
      { name: ClientFields.DEVELOPER, required: false },
      { name: ClientFields.TAXPAYER_IDENTIFICATION_NUMBER, required: true },
      { name: ClientFields.MWBE_TYPE, required: true, computePositive: true },
      { name: ClientFields.TRADES, required: true },
      { name: ClientFields.OTHER_TRADE, required: false },
      { name: ClientFields.UNIVERSAL_BADGE_PRICE, required: false },
    ],
  },
  [ClientStep.CONSENT_FORM_FIELDS]: {
    key: ClientStep.CONSENT_FORM_FIELDS,
    title: 'Consent Form Fields',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 1,
    fields: [],
  },
  [ClientStep.WORKER_PROFILE_FIELDS]: {
    key: ClientStep.WORKER_PROFILE_FIELDS,
    title: 'Worker Profile Fields',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 2,
    fields: [],
  },
  [ClientStep.ADDRESSES]: {
    key: ClientStep.ADDRESSES,
    title: 'Addresses',
    subtitle: 'My Company LCC',
    hideControls: false,
    order: 1,
    fields: [
      {
        name: ClientFields.BILLING_ADDRESS,
        required: true,
        fields: addressFieldRules.required,
      },
      {
        name: ClientFields.MAILING_ADDRESS,
        required: false,
        fields: addressFieldRules.notRequired,
      },
    ],
  },
  [ClientStep.REVIEW]: {
    key: ClientStep.REVIEW,
    title: 'Review',
    subtitle: 'My Company LCC',
    hideControls: true,
    order: 3,
    fields: [],
  },
};

export const developerStepMap = {
  [ClientStep.GENERAL_INFORMATION]: {
    ...clientStepMap[ClientStep.GENERAL_INFORMATION],
    fields: [{ name: ClientFields.NAME, required: true }],
  },
  [ClientStep.ADDRESSES]: {
    ...clientStepMap[ClientStep.ADDRESSES],
    fields: [],
  },
  [ClientFields.USERS]: {
    ...clientStepMap[ClientStep.USERS],
    fields: [],
  },
  [ClientStep.REVIEW]: {
    ...clientStepMap[ClientStep.REVIEW],
    fields: [],
  },
};

export const onboardingDeveloperStepMap = {
  [ClientStep.GENERAL_INFORMATION]: {
    ...clientStepMap[ClientStep.GENERAL_INFORMATION],
    fields: [{ name: ClientFields.NAME, required: true }],
  },
  [ClientStep.ADDRESSES]: {
    ...clientStepMap[ClientStep.ADDRESSES],
    fields: [],
  },
  [ClientStep.REVIEW]: {
    ...clientStepMap[ClientStep.REVIEW],
    fields: [],
  },
};

export enum DetailTabType {
  PROJECTS = 'projects',
  USERS = 'users',
  WORKERS = 'workers',
  INVOICES = 'invoices',
  INFORMATION = 'information',
}

export const tabMap: {
  [key: string]: { id: string; key: string; title: string; permissionsExpression?: string };
} = {
  [DetailTabType.PROJECTS]: {
    id: 'projects',
    key: 'projects',
    title: 'Projects',
    permissionsExpression: UserModel.ClientProjectsPermission.VIEWACCESS,
  },
  [DetailTabType.USERS]: {
    id: 'users',
    key: 'users',
    title: 'Users',
    permissionsExpression: UserModel.UsersPermission.VIEWACCESS,
  },
  [DetailTabType.WORKERS]: {
    id: 'workers',
    key: 'workers',
    title: 'Workers',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  [DetailTabType.INVOICES]: {
    id: 'invoices',
    key: 'invoices',
    title: 'Invoices',
    permissionsExpression: UserModel.InvoicesPermission.VIEWACCESS,
  },
  [DetailTabType.INFORMATION]: {
    id: 'information',
    key: 'information',
    title: 'Information',
    permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
  },
};

export const tabList: { id: string; key: string; title: string; permissionsExpression: string }[] = [
  {
    id: 'projects',
    key: 'projects',
    title: 'Projects',
    permissionsExpression: UserModel.ClientProjectsPermission.VIEWACCESS,
  },
  {
    id: 'users',
    key: 'users',
    title: 'Users',
    permissionsExpression: UserModel.UsersPermission.VIEWACCESS,
  },
  {
    id: 'workers',
    key: 'workers',
    title: 'Workers',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  {
    id: 'invoices',
    key: 'invoices',
    title: 'Invoices',
    permissionsExpression: UserModel.InvoicesPermission.VIEWACCESS,
  },
  {
    id: 'information',
    key: 'information',
    title: 'Information',
    permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
  },
];

export const taxExemptOptionList = [
  { value: 1, label: 'Exempt' },
  { value: 0, label: 'Not Exempt' },
];

export const taxExemptOptionListForInviteClient = [
  { value: 'exempt', label: 'Exempt' },
  { value: 'non-exempt', label: 'Not Exempt' },
];

export const getFallbackClient = (): IClient => ({
  id: null,
  name: '',
  isDeveloper: false,
  relationshipName: '',
  reportsTo: '',
  reportsToName: '',
  taxpayerIdentificationNumber: null,
  mwbeTypeId: '',
  trades: [],
  otherTrade: null,
  users: [],
  billingAddress: getFallbackAddress(),
  mailingAddress: getFallbackAddress(),
  mailingAddressMatchesBillingAddress: true,
  status: CompanyStatus.DRAFT,
  hasUniversalBadge: false,
  universalBadgePrice: null,
  isPatchRequest: false,
  resendInvitation: false,
  project: {} as any,
  sponsor: {} as any,
  taxCondition: '',
  projectsCount: 0,
  companyLabel: 0,
  companyType: 0,
  sponsoredCompaniesIds: [],
  projects: [],
});

export interface IUnverifiedClient {
  [ClientFields.NAME]: string;
  taxCondition?: boolean;
  [ClientFields.TAXPAYER_IDENTIFICATION_NUMBER]?: string;
  projects: any;
}

export const mergeCompanyStepMap = {
  SUMMARY: 1,
  PROJECT: 2,
  USER: 3,
  WORKER: 4,
  INVOICE: 5,
  REPORTS: 6,
  REVIEW: 7,
};

export const mergeSteps = {
  SUMMARY: 'SUMMARY',
  PROJECT: 'PROJECT',
  USER: 'USER',
  UNSELECTED_USER: 'UNSELECTED_USER',
  WORKERS: 'WORKERS',
  INVOICES_SUMMARY: 'INVOICES_SUMMARY',
  BILLING: 'BILLING',
  INVOICES: 'INVOICES',
  REPORTS: 'REPORTS',
  REVIEW: 'REVIEW',
  USER_ADDED: 'USER_ADDED',
  USER_TRANSFERRED: 'USER_TRANSFERRED',
  WORKERS_ADDED: 'WORKERS_ADDED',
  WORKERS_TRANSFERRED: 'WORKERS_TRANSFERRED',
};

export const mergeCompanyStepsName = {
  [mergeCompanyStepMap.SUMMARY]: 'Summary',
  [mergeCompanyStepMap.PROJECT]: 'Projects',
  [mergeCompanyStepMap.USER]: 'Users',
  [mergeCompanyStepMap.WORKER]: 'Workers',
  [mergeCompanyStepMap.INVOICE]: 'Invoices',
  [mergeCompanyStepMap.REPORTS]: 'Reports',
  [mergeCompanyStepMap.REVIEW]: 'Review',
};
